import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { DynamicRule } from '@pdqdev/pdq-shared/dist/entities/Rules';
import {
  mapExtensionRulesToGqlQuery,
  PRODUCT_QUERYABLE_RULE_ENTITIES,
} from '@pdqdev/pdq-shared/dist/utils/extensions-rules-to-graphql';
import { SegmentedControlOption } from '@app/storybook/components/segmented-control/segmented-control.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { ShopService } from '@app/services/shops/shop.service';
import { ProductListItem } from '@storybook/components/product-list/product-list.component';
import {
  AddProductsModalComponent,
  AddProductsModalConfig,
  SelectableProduct,
} from '@app/sharedModule/product-selection/components/add-products-modal/add-products-modal.component';
import {
  EditProductModalComponent,
  EditProductModalData,
  EditProductModalResult,
} from '@app/sharedModule/product-selection/components/edit-product-modal/edit-product-modal.component';
import { ProductSearchService } from '@pages/engage/smart-cart/services/product-search.service';
import { getExtensionRules } from '@pages/checkout-app/checkout-layout/main/components/extensions/shared/all-extension-rules';
import {
  DISCOUNT_LABEL_PLACEHOLDER,
  DISCOUNT_TYPE_OPTIONS,
} from '@pages/engage/smart-cart/smart-cart-form/discount.model';
import {
  HideProductsAlreadyInCart,
  NoItemsToDisplay,
  PrioritizeRecommendedItemsBy,
  ProductsInCartMode,
  UpsellRecommendedProductsMode,
  PRODUCTS_IN_CART_RULE_ENTITIES,
} from '../product-selection.model';

interface PriorityOption {
  value: PrioritizeRecommendedItemsBy;
  label: string;
  description: string;
}

const ADD_PRODUCTS_DIALOG_WIDTH = '700px';
const ADD_PRODUCTS_DIALOG_MAX_HEIGHT = '80vh';
const EDIT_PRODUCT_DIALOG_WIDTH = '650px';

// Stored per rule-group product — mirrors the checkout-upsell extension shape (name-based). `name`/
// `description` are the originals; `displayName`/`displayDescription` are per-product overrides set
// via the edit action (`name` shown in the list resolves to `displayName || name`).
interface UpsellProduct {
  id: string | number;
  handle?: string;
  name: string;
  description?: string;
  displayName?: string;
  displayDescription?: string;
  imageUrl?: string;
  sku?: string;
  price?: string;
  selectedOptions?: { name: string; values: string[] }[];
  priority: number;
}

/**
 * One Product Rule Group card within the Product Selection tab: products-in-cart condition, upsell
 * product selection (automatic / manual / smart), prioritization, restrictions and per-group settings.
 * Operates on its own rule-group FormGroup; FormArray operations (add/remove/duplicate) are the
 * parent's job, so this emits `remove` / `duplicate` rather than touching the array.
 */
@Component({
  selector: 'smart-cart-upsell-rule-group',
  templateUrl: './product-rule-group.component.html',
  styleUrls: ['../product-selection.component.scss'],
  standalone: false,
})
export class SmartCartUpsellRuleGroupComponent {
  @Input({ required: true }) formGroup!: UntypedFormGroup;
  @Input({ required: true }) index!: number;
  @Input() canRemove = false;
  @Input() shopCurrencySymbol = '$';
  @Output() remove = new EventEmitter<void>();
  @Output() duplicate = new EventEmitter<void>();

  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly shopService = inject(ShopService);
  private readonly productSearchService = inject(ProductSearchService);

  // This card has its own "View products" button, so a single boolean is enough for its spinner.
  loading = false;

  readonly productsInCartRules = getExtensionRules({ onlyRules: PRODUCTS_IN_CART_RULE_ENTITIES });
  readonly productsInCartEntities = this.productsInCartRules.map((rule) => rule.entity);
  readonly upsellProductsRules = getExtensionRules({ onlyRules: PRODUCT_QUERYABLE_RULE_ENTITIES });
  readonly upsellProductsEntities = this.upsellProductsRules.map((rule) => rule.entity);

  readonly prioritizeOptions: PriorityOption[] = [
    { value: 'profitability', label: 'Profitability', description: 'Show items with the highest profit margins first' },
    { value: 'high-price', label: 'High Price', description: 'Show most expensive items first' },
    { value: 'low-price', label: 'Low Price', description: 'Show the cheapest items first' },
    { value: 'selling-plan', label: 'Selling plan', description: 'Show items only from a specific selling plan' },
    { value: 'manual', label: 'Manual', description: 'Show items in the order you selected them' },
  ];

  readonly hideProductsOptions: { value: HideProductsAlreadyInCart; label: string; description: string }[] = [
    {
      value: 'parentProduct',
      label: 'Parent product only',
      description: 'Hide if any variant of the product is in cart',
    },
    {
      value: 'specificVariant',
      label: 'Specific variant only',
      description: 'Only hide the exact variant in cart (allow other variants)',
    },
  ];

  readonly ifNoItemsOptions: { value: NoItemsToDisplay; label: string }[] = [
    { value: 'hide', label: 'Hide section' },
    { value: 'showShopifyRecommendations', label: 'Show Shopify recommendations' },
  ];

  readonly productsInCartModeOptions: SegmentedControlOption[] = [
    { label: 'Any Products', value: 'any' },
    { label: 'Smart Selection', value: 'smart' },
  ];

  readonly upsellModeOptions: SegmentedControlOption[] = [
    { label: 'Automatic', value: 'shopifyRecommendations' },
    { label: 'Manual', value: 'manual' },
    { label: 'Smart', value: 'smart' },
  ];

  readonly discountTypeOptions = DISCOUNT_TYPE_OPTIONS;
  readonly discountLabelPlaceholder = DISCOUNT_LABEL_PLACEHOLDER;

  get productsInCartMode(): ProductsInCartMode {
    return this.formGroup.get('productsInCart.mode')?.value ?? 'any';
  }

  setProductsInCartMode(mode: string): void {
    this.formGroup.get('productsInCart.mode')?.setValue(mode);
  }

  get upsellProductsMode(): UpsellRecommendedProductsMode {
    return this.formGroup.get('upsellRecommendedProducts.mode')?.value ?? 'shopifyRecommendations';
  }

  setUpsellProductsMode(mode: string): void {
    this.formGroup.get('upsellRecommendedProducts.mode')?.setValue(mode);
    // Manual prioritization only applies to a manually-ordered list; drop it when leaving manual mode.
    const priorityControl = this.formGroup.get('prioritizeRecommendedItemsBy');
    if (mode !== 'manual' && priorityControl?.value === 'manual') {
      priorityControl.setValue('low-price');
    }
    // Automatic products can't carry a discount, so don't leave an enabled one behind in the payload.
    if (mode === 'shopifyRecommendations') {
      this.formGroup.get('discountConfiguration.enabled')?.setValue(false);
    }
  }

  /** Automatic (Shopify recommendations and Smart) products are unknown up front, so they can't be discounted. */
  get canApplyDiscount(): boolean {
    return this.upsellProductsMode === 'manual';
  }

  get isDiscountEnabled(): boolean {
    return this.formGroup.get('discountConfiguration.enabled')?.value === true;
  }

  get isPercentageDiscount(): boolean {
    return this.formGroup.get('discountConfiguration.discountType')?.value === 'percentage';
  }

  /** Manual prioritization is only meaningful for a manually-picked list, so hide it in other modes. */
  get prioritizeOptionsForMode(): PriorityOption[] {
    if (this.upsellProductsMode === 'manual') return this.prioritizeOptions;
    return this.prioritizeOptions.filter((option) => option.value !== 'manual');
  }

  get products(): UpsellProduct[] {
    return this.formGroup.get('upsellRecommendedProducts.products')?.value ?? [];
  }

  // Single write path — persist with contiguous 0-based priorities in display order so manual ordering stays deterministic.
  private setProducts(products: UpsellProduct[]): void {
    const normalized = products.map((product, index) => ({ ...product, priority: index }));
    this.formGroup.get('upsellRecommendedProducts.products')?.setValue(normalized);
  }

  /** Products mapped for `pdq-product-list` — the displayed name/description resolve overrides. */
  get productsForDisplay(): ProductListItem[] {
    return this.products.map((product) => ({
      ...product,
      name: product.displayName || product.name,
      description: product.displayDescription || product.description,
    }));
  }

  private get upsellRules(): DynamicRule[] {
    return (this.formGroup.get('upsellRecommendedProducts.rules')?.value as DynamicRule[]) ?? [];
  }

  // Shared product search — drops the price for variant-priced products (matches goal-card).
  private async fetchProducts(query: string): Promise<SelectableProduct[]> {
    const products = await firstValueFrom(this.productSearchService.searchProducts(query));
    return products.map((product) => (product.hasUniformPrice === false ? { ...product, price: undefined } : product));
  }

  private toUpsellProduct(product: SelectableProduct, priority: number): UpsellProduct {
    return {
      id: product.id,
      handle: product.handle,
      name: product.title,
      description: product.description,
      imageUrl: product.imageUrl,
      sku: product.sku,
      price: product.price,
      selectedOptions: product.selectedOptions,
      priority,
    };
  }

  /** Manual mode — open the shared add-products modal, then append the chosen products. */
  onAddProducts(): void {
    const current = this.products;
    const config: AddProductsModalConfig = {
      title: 'Add Upsell Products',
      searchPlaceholder: 'Search products by name or SKU...',
      addButtonText: 'Select',
      excludeProductIds: current.map((product) => product.id),
      fetchProducts: (query) => this.fetchProducts(query),
    };

    this.dialog
      .open(AddProductsModalComponent, {
        data: config,
        panelClass: 'add-products-modal-container',
        width: ADD_PRODUCTS_DIALOG_WIDTH,
        maxHeight: ADD_PRODUCTS_DIALOG_MAX_HEIGHT,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: SelectableProduct[] | undefined) => {
        if (!result?.length) return;
        const added = result.map((product, offset) => this.toUpsellProduct(product, current.length + offset));
        this.setProducts([...current, ...added]);
      });
  }

  onRemoveProduct(productId: string | number): void {
    this.setProducts(this.products.filter((product) => product.id !== productId));
  }

  /** Manual prioritization — persist the drag-reordered order (priorities are renormalized in setProducts). */
  onReorderProducts(reorderedProducts: ProductListItem[]): void {
    const current = this.products;
    const reordered = reorderedProducts
      .map((product) => current.find((item) => item.id === product.id))
      .filter((product): product is UpsellProduct => product !== undefined);
    this.setProducts(reordered);
  }

  /** Edit a product's display-name / description override, then persist it back onto the product. */
  onEditProduct(product: ProductListItem): void {
    const editData: EditProductModalData = {
      product: {
        id: product.id,
        name: product.name,
        displayName: (product as UpsellProduct).displayName ?? '',
        description: product.description,
        displayDescription: (product as UpsellProduct).displayDescription ?? '',
        price: product.price,
        sku: product.sku,
        imageUrl: product.imageUrl,
      },
    };
    this.dialog
      .open<EditProductModalComponent, EditProductModalData, EditProductModalResult | undefined>(
        EditProductModalComponent,
        { data: editData, width: EDIT_PRODUCT_DIALOG_WIDTH },
      )
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        this.setProducts(
          this.products.map((item) =>
            item.id === product.id
              ? {
                  ...item,
                  displayName: result.displayName || undefined,
                  displayDescription: result.displayDescription || undefined,
                }
              : item,
          ),
        );
      });
  }

  /** Open the product in the Shopify admin in a new tab. */
  onExternalLink(product: ProductListItem): void {
    const rawId = String(product.id);
    const productId = rawId.includes('/') ? rawId.split('/').pop() : rawId;
    const shopName = this.shopService.shopSignal()?.name?.split('.myshopify.com')[0];
    if (!shopName || !productId) return;
    window.open(`https://admin.shopify.com/store/${shopName}/products/${productId}`, '_blank');
  }

  /** Smart mode — preview the products currently matching this rule group. */
  onViewMatchingProducts(): void {
    // Ignore repeat clicks while a fetch is in flight (avoids a stale response clearing the spinner early).
    if (this.loading) return;
    this.loading = true;
    const query = mapExtensionRulesToGqlQuery(this.upsellRules);
    this.fetchProducts(query)
      .then((results) => {
        this.setProducts(results.map((result, offset) => this.toUpsellProduct(result, offset)));
      })
      .catch(() => this.toastService.showToast({ mode: 'danger', message: 'Failed to load matching products' }))
      .finally(() => (this.loading = false));
  }

  get summary(): string {
    const cart = this.productsInCartMode === 'any' ? 'Any Products' : 'Smart Selection';
    const mode = this.upsellProductsMode;
    let upsell = '';
    if (mode === 'shopifyRecommendations') upsell = 'Automatic';
    else if (mode === 'manual') upsell = `Manual (${this.products.length} products)`;
    else if (mode === 'smart') upsell = 'Smart';
    return `${cart} → ${upsell}`;
  }

  getUpsellProductsHelperText(mode: UpsellRecommendedProductsMode): string {
    if (mode === 'shopifyRecommendations') return 'Uses Shopify recommendations';
    if (mode === 'manual') return 'Add products manually one by one';
    if (mode === 'smart') return 'Existing and future products that match the conditions you set';
    return '';
  }

  get hideProductsValue(): HideProductsAlreadyInCart {
    return this.formGroup.get('hideProductsAlreadyInCart')?.value ?? 'none';
  }

  setHideProductsValue(value: HideProductsAlreadyInCart): void {
    this.formGroup.get('hideProductsAlreadyInCart')?.setValue(value);
  }

  toggleHideProducts(enabled: boolean): void {
    this.setHideProductsValue(enabled ? 'parentProduct' : 'none');
  }
}
